import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useUser } from "../../../Data/UserContext";
import { usePlatformFeatureFlags } from "../../../hooks/usePlatformFeatureFlags";
import { setLeaguesViewer } from "../lib/leagueApiStore";
import { leagueStatus, isLeagueRefunded, leagueJoiningClosed } from "../lib/leagueFormat";
import { apiDataSource } from "../lib/LeaguesDataSource";
import type { League } from "../lib/types";
import { useLeaguesState } from "./useLeaguesPrototype";

export type MyLeaguesSegment = "active" | "ended";

export function useMyLeaguesLists(now: number) {
    const { leagues, vip, simulating } = useLeaguesState();
    // An invite to a league that has finished or been cancelled can never be accepted — the join
    // endpoint rejects it — so it must not sit in the list with a live Join button.
    const invites = useMemo(
        () =>
            leagues.filter(
                (league) => league.membership === "invited" && !leagueJoiningClosed(league, now),
            ),
        [leagues, now],
    );
    const mine = useMemo(
        () => leagues.filter((league) => league.membership === "host" || league.membership === "member"),
        [leagues],
    );
    const { active, ended } = useMemo(() => {
        const activeLeagues: League[] = [];
        const endedLeagues: League[] = [];
        mine.forEach((league) => {
            if (leagueStatus(league, now) === "ended" || isLeagueRefunded(league, now)) endedLeagues.push(league);
            else activeLeagues.push(league);
        });
        const startMs = (league: League) => new Date(league.startsAt).getTime();
        // Same as public hub: soonest match start first (live already-started rows surface first).
        const byStartAsc = (a: League, b: League) => startMs(a) - startMs(b);
        // Past: most recent match start first.
        const byStartDesc = (a: League, b: League) => startMs(b) - startMs(a);
        return { active: activeLeagues.sort(byStartAsc), ended: endedLeagues.sort(byStartDesc) };
    }, [mine, now]);

    return { leagues, invites, mine, active, ended, vip, simulating };
}

export type LeaguesAccess = {
    /** Access is decided; when `canAccess` is false it comes from a fresh server load (safe to redirect). */
    ready: boolean;
    canAccess: boolean;
    vip: boolean;
    /** The viewer's league list is in the store (server or persisted cache) — else show a skeleton. */
    listReady: boolean;
    /** League data APIs need a session; logged-out visitors only get links and the sign-up landing. */
    loggedIn: boolean;
};

/**
 * Leagues nav / tiles are visible to everyone when the Leagues platform flag is on; logged-out
 * visitors land on a sign-up screen. `vip` means "may create leagues and invite friends": any
 * non-guest account. Private invite links stay reachable without this gate.
 */
export function useLeaguesAccess(): LeaguesAccess {
    const { user, loading: userLoading } = useUser();
    const { isLeaguesEnvEnabled, loading: flagsLoading } = usePlatformFeatureFlags();
    const snap = useSyncExternalStore(
        apiDataSource.subscribe,
        apiDataSource.getSnapshot,
        apiDataSource.getSnapshot,
    );
    const userId = user?.userId != null && String(user.userId) !== "" ? String(user.userId) : null;

    useEffect(() => {
        if (userId || !userLoading) setLeaguesViewer(userId);
    }, [userId, userLoading]);

    if (!isLeaguesEnvEnabled) {
        return {
            ready: !flagsLoading && !userLoading,
            canAccess: false,
            vip: false,
            listReady: !userLoading,
            loggedIn: Boolean(userId),
        };
    }

    if (!userId) {
        return { ready: !userLoading, canAccess: !userLoading, vip: false, listReady: !userLoading, loggedIn: false };
    }
    const hydrated = snap.hydratedFor === userId;
    const vip = user?.is_guest !== true;
    return { ready: true, canAccess: true, vip, listReady: hydrated, loggedIn: true };
}

export function openMyLeaguesDrawer(detail?: { games?: MyLeaguesSegment | "past" | "active" }) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("openMyLeaguesDrawer", { detail: detail ?? {} }));
}

/** Listened to by {@link CreateLeagueFlowHost}. */
export const OPEN_CREATE_LEAGUE_MODAL_EVENT = "openCreateLeagueModal";

export type OpenCreateLeagueModalDetail = {
    /** Prefill the match-scoped create flow with this cricket fixture id. */
    fixtureId?: string;
    /**
     * Friends CHALLENGE → Leagues: after create, auto-invite this friend (skip Invite Friends)
     * and navigate to league detail. Existing invite path sends in-app noti + CIO challenge_received.
     */
    challengeFriendUserId?: number;
};

/** Opens create-league modal at app level (survives play drawer close; stacks above header overlay). */
export function openCreateLeagueModal(detail?: OpenCreateLeagueModalDetail) {
    if (typeof window === "undefined") return;
    const fixtureId = typeof detail?.fixtureId === "string" ? detail.fixtureId.trim() : "";
    const rawFriendId = detail?.challengeFriendUserId;
    const challengeFriendUserId =
        typeof rawFriendId === "number" && Number.isFinite(rawFriendId) && rawFriendId > 0
            ? Math.trunc(rawFriendId)
            : undefined;
    const nextDetail: OpenCreateLeagueModalDetail = {};
    if (fixtureId) nextDetail.fixtureId = fixtureId;
    if (challengeFriendUserId != null) nextDetail.challengeFriendUserId = challengeFriendUserId;
    window.dispatchEvent(
        new CustomEvent(OPEN_CREATE_LEAGUE_MODAL_EVENT, {
            detail: nextDetail,
        }),
    );
}
